/**
 * O aviso do sistema operacional, quando o CRM está aberto em outra aba (#536).
 *
 * ── O caso, e por que não é push ─────────────────────────────────────────
 *
 * A pessoa está no navegador, em outra aba. A aba do CRM continua viva, o
 * fluxo SSE continua recebendo, e o card chega — só que ninguém o vê. Aqui a
 * própria página chama a Notification API e o sistema mostra o balão.
 *
 * Não há service worker, nem serviço de push de terceiros no caminho: o
 * conteúdo sai da nossa API para a aba e da aba para o sistema operacional da
 * pessoa. Por isso isto não é o Web Push da #536 (PR 3), que passa por
 * Google/Apple e continua atrás da flag `push_com_conteudo` e do ADR-0017.
 * Com o navegador fechado, nada aqui acontece.
 *
 * ── Nome e mensagem, sem imagem ──────────────────────────────────────────
 *
 * Decidido em 25/09/2026 (docs/decisoes/2026-09-25-aviso-do-sistema-com-a-aba-aberta.md):
 * o card dentro da aba tem foto, nome e mensagem; o aviso do sistema tem só
 * nome e mensagem. Sem `icon` — a foto do lead não vai para a central de
 * notificações do sistema, onde fica guardada fora do nosso alcance.
 *
 * ── Permissão só por gesto ───────────────────────────────────────────────
 *
 * Pedir ao carregar a página faz o navegador bloquear, ou a pessoa negar por
 * reflexo — e negado não se pede de novo. Quem pede é um botão (ver a pilha).
 */

import { useSyncExternalStore } from 'react';
import type { Notificacao } from '@cliqcrm/contracts';
import { mensagemDaNotificacao, nomeDaNotificacao } from '../lib/texto-da-notificacao';

/** `indisponivel`: navegador sem a API, ou contexto sem HTTPS. */
export type PermissaoDoAviso = 'indisponivel' | NotificationPermission;

function api(): typeof Notification | null {
  return typeof Notification === 'undefined' ? null : Notification;
}

/**
 * Onde a pessoa libera o que bloqueou. Daqui não dá para desfazer um
 * bloqueio: o caminho é dito com as mesmas palavras no controle e no convite.
 */
export const COMO_LIBERAR_O_AVISO =
  'Para liberar, clique no ícone à esquerda do endereço, em Notificações escolha Permitir, ' +
  'e volte aqui.';

export function permissaoDoAviso(): PermissaoDoAviso {
  return api()?.permission ?? 'indisponivel';
}

/*
 * Quem mostra a permissão são dois lugares — o rodapé da pilha e o controle
 * de notificações do atendimento —, e o pedido feito num tem de mudar o
 * outro na hora. Por isso ela é um estado compartilhado, e não um `useState`
 * de cada um.
 */
const ouvintesDaPermissao = new Set<() => void>();

function avisarPermissao(): void {
  for (const ouvinte of ouvintesDaPermissao) ouvinte();
}

export async function pedirPermissaoDoAviso(): Promise<PermissaoDoAviso> {
  const Aviso = api();
  if (!Aviso) return 'indisponivel';
  try {
    return await Aviso.requestPermission();
  } catch {
    return Aviso.permission;
  } finally {
    avisarPermissao();
  }
}

/**
 * A permissão também muda FORA da página — a pessoa bloqueia nas
 * configurações do site e volta. A volta à aba é o momento de reler.
 */
function inscreverPermissao(ouvinte: () => void): () => void {
  ouvintesDaPermissao.add(ouvinte);
  document.addEventListener('visibilitychange', ouvinte);
  return () => {
    ouvintesDaPermissao.delete(ouvinte);
    document.removeEventListener('visibilitychange', ouvinte);
  };
}

export function usePermissaoDoAviso(): PermissaoDoAviso {
  return useSyncExternalStore(inscreverPermissao, permissaoDoAviso, () => 'indisponivel');
}

/**
 * Avisa só quando a aba está ESCONDIDA. Com ela à vista, o card basta — e os
 * dois ao mesmo tempo seriam o mesmo aviso duas vezes. E só se a pessoa quer
 * (a preferência) E o navegador deixa (a permissão): as duas são independentes,
 * e desligar no CRM não mexe na permissão que ela deu ao navegador.
 */
export function deveAvisarNoSistema(situacao: {
  readonly abaVisivel: boolean;
  readonly permissao: PermissaoDoAviso;
  readonly foraDaTela: boolean;
}): boolean {
  return situacao.foraDaTela && !situacao.abaVisivel && situacao.permissao === 'granted';
}

/** Com a mesma `tag`, o sistema troca o balão em vez de empilhar outro igual. */
export function etiquetaDoAviso(notificacao: Notificacao): string {
  return `cliqcrm:notificacao:${notificacao.id}`;
}

export function opcoesDoAviso(notificacao: Notificacao): {
  readonly titulo: string;
  readonly opcoes: NotificationOptions;
} {
  return {
    titulo: nomeDaNotificacao(notificacao),
    opcoes: {
      body: mensagemDaNotificacao(notificacao),
      tag: etiquetaDoAviso(notificacao),
      /*
       * Mudo: quem toca é o sino, que respeita o "silenciar" da pessoa. O som
       * do sistema por cima tocaria duas vezes — e continuaria tocando para
       * quem silenciou o sino.
       */
      silent: true,
    },
  };
}

/**
 * Mostra o balão. O clique traz a aba para frente e abre a conversa.
 *
 * Tudo dentro de `try`: no Chrome do Android o construtor LANÇA ("use
 * ServiceWorkerRegistration.showNotification"). Celular não é o alvo aqui, e
 * lá o card continua valendo quando a pessoa voltar.
 */
export function avisarNoSistema(notificacao: Notificacao, aoClicar: () => void): void {
  const Aviso = api();
  if (!Aviso) return;
  const { titulo, opcoes } = opcoesDoAviso(notificacao);
  try {
    const aviso = new Aviso(titulo, opcoes);
    aviso.onclick = () => {
      window.focus();
      aoClicar();
      aviso.close();
    };
  } catch {
    // Ver o comentário da função.
  }
}
